import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getChallengeTableColumns } from "utils/tableHelper/challengeTableHelper";
import sessionHelper from "services/sessionHelper";
import {
  copyChallenge,
  deleteChallenge,
  getChallenges,
  updateChallengeStatus,
} from "services/challengeService";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";
import { SimpleSelectInput } from "components/FormSelectInput";

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  filtersRow: {
    display: "flex",
    flexFlow: "row nowrap",
    gap: 8,
    alignItems: "center",
    marginBottom: 8,
  },
  filterSelect: {
    minWidth: 140,
  },
}));

const typeFilterOptions = [
  { id: "", label: "All Types" },
  { id: "INDIVIDUAL", label: "Individual" },
  { id: "COMBO", label: "Combo" },
];

const tierFilterOptions = [
  { id: "", label: "All Tiers" },
  { id: "STANDARD", label: "Standard" },
  { id: "PREMIUM", label: "Premium" },
  { id: "OTHER", label: "Other" },
];

const statusFilterOptions = [
  { id: "", label: "All Statuses" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDisble?: (id: any) => void;
    handleDelete?: (data: any) => void;
    handleCopyChallenge?: (id: any) => void;
  };
}

const RowActions = ({
  rowId,
  data,
  actionHandlers: { handleDisble = () => {}, handleDelete = () => {}, handleCopyChallenge = () => {} },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(
      configData.routes.challenges.editChallenges?.replace(":id", data?._id),
    );
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.CHALLENGES.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        <MenuItem onClick={() => handleDisble(data?._id)}>
          {data?.isActive ? "Disable" : "Enable"}
        </MenuItem>
        <MenuItem onClick={() => handleCopyChallenge(data?._id)}>Copy</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
        )}
      </Menu>
    </>
  );
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const Challenges = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [typeFilter, setTypeFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showExpired, setShowExpired] = useState(false);

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const onSearch = (value: any) => {
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.CHALLENGES.UPDATE_ROLES,
  );

  const navigateToCreate = () =>
    navigate(configData.routes.challenges.createChallenges);

  const navigateToBulkUpload = () =>
    navigate(configData.routes.challenges.bulkUploadChallenges);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onDelete = async () => {
    try {
      const resp = await deleteChallenge(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

    const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copyChallenge(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({
      itemName: data?.objectiveType ?? data?._id,
      id: data?._id,
    });
  };

  const onDisable = async (challengeId: any) => {
    try {
      const resp = await updateChallengeStatus(challengeId);
      showSnackbar(resp?.data?.message);
      fetchData();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let payload: any = { pageNo, pageSize, expired: showExpired };
      if (searchValue) payload.search = searchValue;
      if (typeFilter) payload.type = typeFilter;
      if (tierFilter) payload.tier = tierFilter;
      if (statusFilter) payload.status = statusFilter;

      const resp = await getChallenges(payload);

      setTotalItems(resp?.data?.totalItems ?? 0);
      setListData(
        resp?.data?.items?.map((item: any) => ({
          ...item,
          isActiveText: item.isActive ? "YES" : "NO",
          startDateText: formatDate(item.startDate),
          endDateText: formatDate(item.endDate),
          territoriesText:
            !item.territories?.length ? "All" : item.territories.join(", "),
          objectiveType:
            item.type === "COMBO"
              ? `COMBO (${item.subChallenges?.length || 0} sub)`
              : item.objectiveType || "—",
          // objectiveTarget:
          //   item.type === "COMBO" ? "—" : (item.objectiveTarget ?? "—"),
          rowDisabled: !item?.isActive,
          copiedFrom: item?.copiedFrom || "-",
        })) || [],
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const cols = getChallengeTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(cols);
    fetchData();
  }, [
    pageNo,
    pageSize,
    searchValue,
    hasEditPermission,
    typeFilter,
    tierFilter,
    statusFilter,
    showExpired,
  ]);

  return (
    <ContentShell
      title="Challenges"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search challenges"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.CHALLENGES.CREATE_ROLES) && (
            <CustomButton onClick={navigateToBulkUpload}>
              Bulk Upload
            </CustomButton>
          )}
          {sessionHelper.hasRole(PermittedRoles.CHALLENGES.CREATE_ROLES) && (
            <CustomButton onClick={navigateToCreate}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.filtersRow}>
          <Box className={styles.filterSelect}>
            <SimpleSelectInput
              label="Type"
              name="typeFilter"
              value={typeFilter}
              options={typeFilterOptions}
              onChange={(e: any) => {
                setTypeFilter(e.target.value);
                onChangePage(defaultPageNo);
              }}
            />
          </Box>
          <Box className={styles.filterSelect}>
            <SimpleSelectInput
              label="Tier"
              name="tierFilter"
              value={tierFilter}
              options={tierFilterOptions}
              onChange={(e: any) => {
                setTierFilter(e.target.value);
                onChangePage(defaultPageNo);
              }}
            />
          </Box>
          <Box className={styles.filterSelect}>
            <SimpleSelectInput
              label="Status"
              name="statusFilter"
              value={statusFilter}
              options={statusFilterOptions}
              onChange={(e: any) => {
                setStatusFilter(e.target.value);
                onChangePage(defaultPageNo);
              }}
            />
          </Box>
          <FormControlLabel
            sx={{ ml: "auto", mr: 0 }}
            control={
              <Switch
                checked={showExpired}
                onChange={(e) => {
                  setShowExpired(e.target.checked);
                  onChangePage(defaultPageNo);
                }}
              />
            }
            label="Expired Challenges"
          />
        </Box>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalItems}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          actionHandlers={{
            handleDisble: onDisable,
            handleDelete: _openConsentDialog,
            handleCopyChallenge: onCopy,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default Challenges;
